import { useToast } from '@/composables/useToast';
import i18n from '@/locale/i18n';

/** The `inertia:networkError` event, as `router.on('networkError', …)` delivers it. */
export type NetworkErrorEvent = CustomEvent<{ error: Error }>;

/** The global error net's reporter — see the `reportUncaught` closure in `app.ts`. */
type ReportUncaught = (message: string, context: Record<string, unknown>) => void;

/**
 * Own an Inertia visit that never reached the server.
 *
 * Inertia leaves such a visit's promise REJECTED unless a listener cancels the
 * (cancelable) `networkError` event: `Request.send()` ends its catch chain in
 * `Promise.reject(error)`, and `RequestStream` attaches only a `.finally()` —
 * nothing downstream catches it. So every dropped connection reached Sentry as
 * an unhandled `HttpNetworkError: Network error` via the browser's
 * `unhandledrejection` handler: unactionable, because the fault is the
 * visitor's connection rather than this application.
 *
 * It was worse for the visitor than for us. The failed navigation was silent —
 * the progress bar disappeared and the page they had clicked away from simply
 * stayed put, with nothing to say the click had been lost.
 *
 * Preventing the default is Inertia's own contract for taking responsibility
 * for the failure, so this handler does all three things at once: report it to
 * the unified log, tell the visitor in a toast, and stop the rejection here.
 */
export const createNetworkErrorHandler =
    (report: ReportUncaught) =>
    (event: NetworkErrorEvent): void => {
        const { error } = event.detail;

        report(error instanceof Error ? error.message : String(error), { source: 'inertia.networkError' });

        useToast().show(i18n.global.t('errors.network_error'), 'warning');

        event.preventDefault();
    };
